import type { FirstTierSupplier } from "@/api/supplierService";
import Breadcrumb from "@/components/Breadcrumb";
import BackButton from "@/components/buttons/BackButton";
import SupplierDetailCard from "@/components/first-tier-supplier/SupplierDetailCard";
import {
  useGetFirstTierSuppliers,
  useGetPartSKPBySupplier,
} from "@/hooks/supplierHooks";
import { formattedDateTime } from "@/utils/date";
import { Box, Button, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import BreakdownExchangeRateTable from "./BreakdownExchangeRateTable";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { usePermissions } from "@/hooks/usePermissions";
import { useUserInfo } from "@/context/UserInfoContext";
import {
  DefaultPrimaryButton,
  DefaultSecondaryButton,
  NormalSecondaryButton,
} from "@/styles/globalstyles";
import { Add, InsertDriveFile } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import {
  useGetBreakdownExchangeRateMaster,
  useGetBreakdownExchangeRatePart,
  useGetBreakdownExchangeRateProcess,
  useBulkImportBreakdownExchangeRateParts,
} from "@/hooks/breakdownExchangeRateHooks";
import type {
  exchangeRatePart,
  exchangeRateBulkImportItem,
  CurrencyOption,
} from "@/api/breakdownExchangeRateService";
import ActivateButton from "@/components/buttons/ActivateButton";
import { useBreakdownExchangeRateForm } from "@/hooks/useBreakdownExchangeRateForm";
import { useFlagPartHandler } from "@/hooks/useFlagPartHandler";
import UnFlaggedModal from "@/components/flagged-part/UnFlaggedModal";
import ExchangeRatePartHistoryModal from "@/components/exchange-rate/ExchangeRatePartHistoryModal";
import ManageExchangeRateOtherProcessModal from "@/components/first-tier-supplier/breakdown-exchange-rate/ManageExchangeRateOtherProcessModal";
import ImportExchangeRatePartModal from "@/components/first-tier-supplier/breakdown-exchange-rate/ImportExchangeRatePartModal";
import ImportExchangeRatePartPreviewModal from "@/components/first-tier-supplier/breakdown-exchange-rate/ImportExchangeRatePartPreviewModal";
import { ConfirmAlert, ErrorAlert } from "@/utils/alert";
import { exportAllExchangeRatePartToExcel } from "@/utils/excel/index";
import {
  buildExchangeRateLabel,
  parseExchangeRatePartFromExcel,
  type ParsedExchangeRateImportResult,
} from "@/utils/excel/importPartExchangeRate";
import { useToast } from "@/hooks/useToast";
import { getAxiosErrorMessage } from "@/utils/healper";
import { Upload } from "@mui/icons-material";

const BreakdownExchangeRate = () => {
  const navigate = useNavigate();
  const canEdit = usePermissions().canEdit("breakdown");
  const [supplier, setSupplier] = useState<FirstTierSupplier | null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpenUnFlagModal, setIsOpenUnFlagModal] = useState(false);
  const [isOpenHistoryModal, setIsOpenHistoryModal] = useState(false);
  const [isFetchingForDownload, setIsFetchingForDownload] = useState(false);
  const [managePartOtherProcessModalOpen, setManagePartOtherProcessModalOpen] =
    useState<boolean>(false);
  const [flagPartId, setFlagPartId] = useState<number | null>(null);
  const [historyPartId, setHistoryPartId] = useState<number | null>(null);
  const [historyPartNumber, setHistoryPartNumber] = useState<string>("");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [parsedImportData, setParsedImportData] =
    useState<ParsedExchangeRateImportResult | null>(null);
  const [isParsingImport, setIsParsingImport] = useState(false);
  const { supplierCode } = useParams<{ supplierCode: string }>();
  const userInfo = useUserInfo();
  const { showToast } = useToast();

  const canImport = userInfo?.groups?.some(
    (g) => g.toLowerCase() === "developer",
  );

  const activeTab = useMemo(
    () => Number(searchParams.get("active")) || 0,
    [searchParams],
  );
  const page = useMemo(
    () => Number(searchParams.get("page")) || 0,
    [searchParams],
  );
  const rowsPerPage = useMemo(
    () => Number(searchParams.get("limit")) || 10,
    [searchParams],
  );

  // ── Form state & handlers (custom hook) ──

  const {
    isAdding,
    isEditing,
    newPart,
    editedPart,
    selectedParts,
    validationErrors,
    isCreatingExchangeRatePart,
    isUpdatingExchangeRatePart,
    setNewPart,
    setEditedPart,
    setSelectedParts,
    handleAddPart,
    handleEditPart,
    handleCancel,
    handleSavePart,
    handleSaveEditedPart,
    handleActivatePart,
    handleTabReset,
  } = useBreakdownExchangeRateForm(supplier?.supplier_code || "");

  // ── Data fetching ──

  const {
    data: firstTierSuppliers,
    isLoading: isLoadingFirstTierSuppliers,
    isError: isErrorFirstTierSuppliers,
  } = useGetFirstTierSuppliers({
    skip: 0,
    limit: 2000,
    buyer_code: userInfo?.buyer_code,
  });

  const {
    data: partSKPData,
    isLoading: isLoadingPartSKP,
    isError: isErrorPartSKP,
  } = useGetPartSKPBySupplier(
    supplierCode && supplier ? supplier.supplier_code : "",
  );

  const {
    data: exchangeRateMaster,
    isLoading: isLoadingExchangeRateMaster,
    isError: isErrorExchangeRateMaster,
  } = useGetBreakdownExchangeRateMaster();

  const {
    data: exchangeRateParts,
    isLoading: isLoadingExchangeRateParts,
    isError: isErrorExchangeRateParts,
  } = useGetBreakdownExchangeRatePart(
    supplierCode && supplier ? supplier.supplier_code : "",
    activeTab === 0,
  );

  const {
    data: exchangeRateProcess,
    isLoading: isLoadingExchangeRateProcess,
    isError: isErrorExchangeRateProcess,
  } = useGetBreakdownExchangeRateProcess(supplierCode || "");

  const { handleFlagPart, isFlagging } = useFlagPartHandler({
    onOpenUnflagModal: (partId) => {
      setFlagPartId(partId);
      setIsOpenUnFlagModal(true);
    },
  });

  const { mutate: bulkImportExchangeRateParts, isPending: isBulkImporting } =
    useBulkImportBreakdownExchangeRateParts();

  useQueryHandler({
    isError:
      isErrorFirstTierSuppliers ||
      isErrorExchangeRateMaster ||
      isErrorExchangeRateParts ||
      isErrorExchangeRateProcess ||
      isErrorPartSKP,
    isFetching:
      isLoadingFirstTierSuppliers ||
      isLoadingExchangeRateMaster ||
      isLoadingPartSKP ||
      isLoadingExchangeRateProcess ||
      isCreatingExchangeRatePart ||
      isUpdatingExchangeRatePart ||
      isFlagging ||
      isFetchingForDownload ||
      isParsingImport ||
      isBulkImporting,
  });

  const currencyOptions: CurrencyOption[] = useMemo(() => {
    if (!exchangeRateMaster || !supplier) {
      return [];
    }
    return exchangeRateMaster.map((rate) => ({
      label: buildExchangeRateLabel(rate, supplier.supplier_abbr),
      value: {
        exchange_rate_supplier_id: rate.exchange_rate_supplier_id,
        currency_id: rate.currency_id,
        exchange_rate: rate.exchange_rate,
        rounding: rate.rounding.replace("_", " ") as
          | "ROUND"
          | "ROUND UP"
          | "ROUND DOWN",
        rounding_digit: rate.rounding_digit,
      },
    }));
  }, [exchangeRateMaster, supplier]);

  const { sortedOEMOtherProcess, sortedSpareOtherProcess } = useMemo(() => {
    return {
      sortedOEMOtherProcess: exchangeRateProcess?.oem_other_process
        ? [...exchangeRateProcess.oem_other_process].sort((a, b) => a.id - b.id)
        : [],
      sortedSpareOtherProcess: exchangeRateProcess?.spare_other_process
        ? [...exchangeRateProcess.spare_other_process].sort(
            (a, b) => a.id - b.id,
          )
        : [],
    };
  }, [exchangeRateProcess]);

  useEffect(() => {
    document.title = "Breakdown Exchange Rate | AMPA";
  }, []);

  useEffect(() => {
    if (firstTierSuppliers && supplierCode) {
      const foundSupplier = firstTierSuppliers.find(
        (sup) => sup.supplier_code === supplierCode,
      );
      setSupplier(foundSupplier || null);
      if (
        !foundSupplier ||
        !foundSupplier.template_type ||
        foundSupplier.template_type.toLowerCase() !== "exchange rate"
      ) {
        navigate("/breakdown");
      }
    }
  }, [supplierCode, firstTierSuppliers, navigate]);

  const { filteredParts, totalFilteredCount } = useMemo(() => {
    if (!exchangeRateParts) {
      return { filteredParts: [], totalFilteredCount: 0 };
    }

    let filtered: exchangeRatePart[] = exchangeRateParts;
    if (searchFilter) {
      const search = searchFilter.toLowerCase();
      filtered = filtered.filter(
        (part) =>
          part.part_number.toLowerCase().includes(search) ||
          part.part_name.toLowerCase().includes(search),
      );
    }

    const totalCount = filtered.length;
    const startIndex = page * rowsPerPage;
    filtered = filtered.slice(startIndex, startIndex + rowsPerPage);

    return { filteredParts: filtered, totalFilteredCount: totalCount };
  }, [exchangeRateParts, searchFilter, page, rowsPerPage]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    handleTabReset();
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("active", String(newValue));
      newParams.set("page", String(0));
      return newParams;
    }, { replace: true });
  };

  const handleImportFile = async (file: File) => {
    if (!supplier) return;
    setIsParsingImport(true);
    try {
      const parsed = await parseExchangeRatePartFromExcel(
        file,
        supplier.supplier_code,
      );
      setParsedImportData(parsed);
      setImportPreviewOpen(true);
    } catch (e) {
      ErrorAlert("Error", getAxiosErrorMessage(e));
    } finally {
      setIsParsingImport(false);
    }
  };

  const handleConfirmImport = () => {
    if (!parsedImportData || !supplier) return;
    const body: exchangeRateBulkImportItem[] = parsedImportData.rows.map((r) => ({
      part_id: r.part_id,
      exchange_rate_supplier_id: r.exchange_rate_supplier_id,
      currency_id: r.currency_id,
      currency_code: r.currency_code,
      price: r.price,
      is_active: true,
      oem_other_process: r.oem_other_process,
      spare_other_process: r.spare_other_process,
    }));
    bulkImportExchangeRateParts(
      { supplierCode: supplier.supplier_code, data: body },
      {
        onSuccess: () => {
          showToast(`Import สำเร็จ ${body.length} รายการ`, "success");
          setImportPreviewOpen(false);
          setParsedImportData(null);
        },
        onError: (e) => ErrorAlert("Error", getAxiosErrorMessage(e)),
      },
    );
  };

  const handleOpenHistory = (partId: number, partNumber?: string) => () => {
    setHistoryPartId(partId);
    setHistoryPartNumber(partNumber || "");
    setIsOpenHistoryModal(true);
  };

  const handleDownloadReport = async () => {
    const confirm = await ConfirmAlert(
      "Download Report",
      "ต้องการดาวน์โหลดรายงานใช่หรือไม่?",
      "ยืนยัน",
      "ยกเลิก",
    );
    if (!confirm) return;

    setIsFetchingForDownload(true);
    try {
      await exportAllExchangeRatePartToExcel({
        supplierCode: supplier?.supplier_code || "",
        supplierName: supplier?.supplier_name || "",
        buyerCode: supplier?.buyer_code_list?.[0] || "",
        parts: exchangeRateParts || [],
      });
    } finally {
      setIsFetchingForDownload(false);
    }
  };

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb
        current={supplier?.supplier_abbr}
        previous={[{ key: "breakdown" }]}
      />
      <Box mt={1}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          gap={2}
        >
          <BackButton to={`/breakdown/${supplierCode}`} />
        </Box>
        <Box mt={1} display="flex" flexDirection="row" gap={2} width="100%">
          <SupplierDetailCard
            title={supplier?.supplier_code || ""}
            text={supplier?.supplier_name || ""}
          />
          <SupplierDetailCard
            title="Buyer ID"
            text={supplier?.buyer_code_list?.join(", ") || ""}
          />
          <SupplierDetailCard
            title="Last Updated"
            text={formattedDateTime(supplier?.update_date || "")}
          />
          <SupplierDetailCard
            title="Active Parts"
            text={supplier?.active_part?.toString() || "0"}
          />
        </Box>
        <Box mt={2}>
          <TextField
            label="ค้นหา Part Number / Part Name"
            variant="outlined"
            size="small"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            sx={{
              width: 300,
              "& .MuiInputLabel-root": {
                fontSize: "0.8rem",
                color: "#666666",
                "&.Mui-focused": { color: "primary.main" },
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#cccccc" },
                "&:hover fieldset": { borderColor: "#999999" },
              },
            }}
          />
        </Box>
        <Box
          mt={1}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Tabs
            value={activeTab}
            aria-label="exchange rate tabs"
            onChange={handleTabChange}
          >
            <Tab label="Active" disabled={isAdding || isEditing} />
            <Tab label="Inactive" disabled={isAdding || isEditing} />
          </Tabs>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={1}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              fontSize="0.9rem"
              mr={1}
            >
              Selected {selectedParts?.length || 0} items
            </Typography>
            {isAdding || isEditing ? (
              <>
                <Button
                  id="cancel-btn"
                  variant="outlined"
                  sx={{ ...NormalSecondaryButton, fontSize: "0.75rem" }}
                  onClick={handleCancel}
                >
                  ยกเลิก
                </Button>
                <Button
                  id="save-btn"
                  variant="outlined"
                  sx={{ ...DefaultPrimaryButton, fontSize: "0.75rem" }}
                  onClick={isAdding ? handleSavePart : handleSaveEditedPart}
                >
                  บันทึก
                </Button>{" "}
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    ...DefaultSecondaryButton,
                    height: "32px",
                    fontSize: "0.75rem",
                    color: "secondary.main",
                    borderColor: "secondary.main",
                  }}
                  onClick={() => setManagePartOtherProcessModalOpen(true)}
                >
                  จัดการ Other Process
                </Button>
                {canImport && activeTab === 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      ...DefaultSecondaryButton,
                      fontSize: "0.75rem",
                    }}
                    onClick={() => setImportModalOpen(true)}
                  >
                    <Upload fontSize="small" /> Import
                  </Button>
                )}
                <Button
                  variant="outlined"
                  sx={{
                    ...DefaultSecondaryButton,
                    fontSize: "0.75rem",
                    
                  }}
                  size="small"
                  onClick={handleDownloadReport}
                >
                  <InsertDriveFile /> Download
                </Button>
                <ActivateButton
                  type={activeTab}
                  disabled={selectedParts.length === 0 || !canEdit}
                  onClick={() => {
                    handleActivatePart(activeTab === 1);
                  }}
                />
                {activeTab === 0 && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        ...DefaultSecondaryButton,
                        fontSize: "0.75rem",
                        width: 130,
                      }}
                      onClick={handleEditPart}
                      disabled={selectedParts?.length === 0 || !canEdit}
                    >
                      <EditIcon />
                      แก้ไข Part
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        ...DefaultSecondaryButton,
                        fontSize: "0.75rem",
                        width: 130,
                      }}
                      onClick={handleAddPart}
                      disabled={!canEdit}
                    >
                      <Add /> เพิ่ม Part
                    </Button>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
        <Box mt={1}>
          <BreakdownExchangeRateTable
            rows={filteredParts}
            rowCount={totalFilteredCount}
            isLoading={isLoadingExchangeRateParts}
            page={page}
            setPage={(newPage) => {
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("page", String(newPage));
                return newParams;
              }, { replace: true });
            }}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(newRowsPerPage) => {
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("limit", String(newRowsPerPage));
                newParams.set("page", String(0));
                return newParams;
              }, { replace: true });
            }}
            selectedParts={selectedParts}
            setSelectedParts={setSelectedParts}
            handleFlagPart={handleFlagPart}
            handleOpenHistory={handleOpenHistory}
            canEdit={canEdit}
            currencyOptions={currencyOptions}
            isAdding={isAdding}
            isEditing={isEditing}
            newPart={newPart}
            setNewPart={setNewPart}
            editedPart={editedPart}
            setEditedPart={setEditedPart}
            partOptions={partSKPData || []}
            validationErrors={validationErrors}
            oemProcess={sortedOEMOtherProcess}
            spareProcess={sortedSpareOtherProcess}
          />
        </Box>
      </Box>

      <UnFlaggedModal
        open={isOpenUnFlagModal}
        handleClose={() => {
          setIsOpenUnFlagModal(false);
          setFlagPartId(null);
        }}
        selectedParts={[
          {
            partId: flagPartId ?? 0,
          },
        ]}
        requiredApprovalId={true}
      />

      <ExchangeRatePartHistoryModal
        open={isOpenHistoryModal}
        handleClose={() => {
          setIsOpenHistoryModal(false);
          setHistoryPartId(null);
          setHistoryPartNumber("");
        }}
        partId={historyPartId}
        partNumber={historyPartNumber}
      />

      <ManageExchangeRateOtherProcessModal
        open={managePartOtherProcessModalOpen}
        handleClose={() => setManagePartOtherProcessModalOpen(false)}
        supplierCode={supplier?.supplier_code}
        canEdit={canEdit}
      />

      <ImportExchangeRatePartModal
        open={importModalOpen}
        handleClose={() => setImportModalOpen(false)}
        onImport={handleImportFile}
      />

      <ImportExchangeRatePartPreviewModal
        open={importPreviewOpen}
        data={parsedImportData}
        onClose={() => {
          setImportPreviewOpen(false);
          setParsedImportData(null);
        }}
        onConfirm={handleConfirmImport}
        isSubmitting={isBulkImporting}
      />
    </Box>
  );
};

export default BreakdownExchangeRate;
